import React from 'react';
import {BrowserRouter as Router, Routes, Route, Navigate} from 'react-router-dom';
import {Toaster} from 'react-hot-toast';
import {AuthProvider, useAuth} from './hooks/useAuth';
import {ThemeProvider} from './hooks/useTheme';
import {DensityProvider} from './hooks/useDensity';
import {useGlobalShortcuts} from './hooks/useGlobalShortcuts';
import {CommandPaletteProvider} from './components/layout/CommandPalette';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/layout/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import RequesterList from './pages/requesters/RequesterList';
import RequesterCreate from './pages/requesters/RequesterCreate';
import RequesterEdit from './pages/requesters/RequesterEdit';
import TaskList from './pages/tasks/TaskList';
import TaskCreate from './pages/tasks/TaskCreate';
import TaskEdit from './pages/tasks/TaskEdit';
import TaskView from './pages/tasks/TaskView';
import ProjectList from './pages/projects/ProjectList';
import ProjectCreate from './pages/projects/ProjectCreate';
import ProjectEdit from './pages/projects/ProjectEdit';
import ModuleList from './pages/modules/ModuleList';
import ModuleCreate from './pages/modules/ModuleCreate';
import ModuleEdit from './pages/modules/ModuleEdit';
import ServerList from './pages/servers/ServerList';
import ServerCreate from './pages/servers/ServerCreate';
import ServerEdit from './pages/servers/ServerEdit';
import DeliveryList from './pages/deliveries/DeliveryList';
import DeliveryCreate from './pages/deliveries/DeliveryCreate';
import DeliveryEdit from './pages/deliveries/DeliveryEdit';
import DeliveryView from './pages/deliveries/DeliveryView';
import BillingMonthManagement from './pages/billing/BillingMonthManagement';
import PrioritiesBoard from './pages/priorities/PrioritiesBoard';
import ProfileManagement from './pages/profiles/ProfileManagement';
import NotificationList from './pages/notifications/NotificationList';
import ParameterList from './pages/parameters/ParameterList';
import {UserSettings} from './pages/UserSettings';
import NotFound from './pages/NotFound';
import PWAManager from './components/pwa/PWAManager';

const RedirectIfAuthenticated: React.FC<{children: React.ReactNode}> = ({children}) => {
    const {isAuthenticated, isLoading} = useAuth();
    if (isLoading) {
        return (
            <div className="min-h-dvh bg-surface-app flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-2 border-accent border-t-transparent mx-auto"></div>
                    <p className="mt-4 text-text-secondary text-sm">Carregando...</p>
                </div>
            </div>
        );
    }
    if (isAuthenticated) return <Navigate to="/dashboard" replace/>;
    return <>{children}</>;
};

/** Componente "thunk" que monta CommandPalette/atalhos depois do AuthProvider + Router. */
const AppShell: React.FC = () => {
    useGlobalShortcuts();
    return (
        <CommandPaletteProvider>
            <Routes>
                <Route
                    path="/login"
                    element={
                        <RedirectIfAuthenticated>
                            <Login/>
                        </RedirectIfAuthenticated>
                    }
                />
                <Route path="/*" element={
                    <ProtectedRoute>
                        <Layout>
                            <Routes>
                                <Route path="/dashboard" element={<Dashboard/>}/>
                                <Route path="/requesters" element={<ProtectedRoute requiredProfile="ADMIN"><RequesterList/></ProtectedRoute>}/>
                                <Route path="/requesters/create" element={<ProtectedRoute requiredProfile="ADMIN"><RequesterCreate/></ProtectedRoute>}/>
                                <Route path="/requesters/:id/edit" element={<ProtectedRoute requiredProfile="ADMIN"><RequesterEdit/></ProtectedRoute>}/>
                                <Route path="/tasks" element={<ProtectedRoute requiredProfiles={['ADMIN','MANAGER','USER']}><TaskList/></ProtectedRoute>}/>
                                <Route path="/tasks/create" element={<ProtectedRoute requiredProfiles={['ADMIN','MANAGER','USER']}><TaskCreate/></ProtectedRoute>}/>
                                <Route path="/tasks/:id/edit" element={<ProtectedRoute requiredProfiles={['ADMIN','MANAGER','USER']}><TaskEdit/></ProtectedRoute>}/>
                                <Route path="/tasks/:id" element={<ProtectedRoute requiredProfiles={['ADMIN','MANAGER','USER']}><TaskView/></ProtectedRoute>}/>
                                <Route path="/projects" element={<ProtectedRoute requiredProfile="ADMIN"><ProjectList/></ProtectedRoute>}/>
                                <Route path="/projects/create" element={<ProtectedRoute requiredProfile="ADMIN"><ProjectCreate/></ProtectedRoute>}/>
                                <Route path="/projects/:id/edit" element={<ProtectedRoute requiredProfile="ADMIN"><ProjectEdit/></ProtectedRoute>}/>
                                <Route path="/modules" element={<ProtectedRoute requiredProfile="ADMIN"><ModuleList/></ProtectedRoute>}/>
                                <Route path="/modules/create" element={<ProtectedRoute requiredProfile="ADMIN"><ModuleCreate/></ProtectedRoute>}/>
                                <Route path="/modules/:id/edit" element={<ProtectedRoute requiredProfile="ADMIN"><ModuleEdit/></ProtectedRoute>}/>
                                <Route path="/servers" element={<ProtectedRoute requiredProfile="ADMIN"><ServerList/></ProtectedRoute>}/>
                                <Route path="/servers/create" element={<ProtectedRoute requiredProfile="ADMIN"><ServerCreate/></ProtectedRoute>}/>
                                <Route path="/servers/:id/edit" element={<ProtectedRoute requiredProfile="ADMIN"><ServerEdit/></ProtectedRoute>}/>
                                <Route path="/deliveries" element={<ProtectedRoute requiredProfiles={['ADMIN','MANAGER','USER']}><DeliveryList/></ProtectedRoute>}/>
                                <Route path="/deliveries/create" element={<ProtectedRoute requiredProfiles={['ADMIN','MANAGER','USER']}><DeliveryCreate/></ProtectedRoute>}/>
                                <Route path="/deliveries/:deliveryId/edit" element={<ProtectedRoute requiredProfiles={['ADMIN','MANAGER','USER']}><DeliveryEdit/></ProtectedRoute>}/>
                                <Route path="/deliveries/:id" element={<ProtectedRoute requiredProfiles={['ADMIN','MANAGER','USER']}><DeliveryView/></ProtectedRoute>}/>
                                <Route path="/billing" element={<ProtectedRoute requiredProfiles={['ADMIN','MANAGER']}><BillingMonthManagement/></ProtectedRoute>}/>
                                <Route path="/priorities" element={<ProtectedRoute requiredProfiles={['ADMIN','MANAGER','USER']}><PrioritiesBoard/></ProtectedRoute>}/>
                                <Route path="/profiles" element={<ProtectedRoute requiredProfile="ADMIN"><ProfileManagement/></ProtectedRoute>}/>
                                <Route path="/notifications" element={<ProtectedRoute requiredProfile="ADMIN"><NotificationList/></ProtectedRoute>}/>
                                <Route path="/parameters" element={<ProtectedRoute requiredProfile="ADMIN"><ParameterList/></ProtectedRoute>}/>
                                <Route path="/settings" element={<UserSettings/>}/>
                                <Route path="/" element={<Navigate to="/dashboard" replace/>}/>
                                <Route path="*" element={<NotFound/>}/>
                            </Routes>
                        </Layout>
                    </ProtectedRoute>
                }/>
            </Routes>
        </CommandPaletteProvider>
    );
};

const App: React.FC = () => {
    return (
        <ThemeProvider>
            <DensityProvider>
                <Router future={{v7_startTransition: true, v7_relativeSplatPath: true}}>
                    <AuthProvider>
                        <AppShell/>
                        <PWAManager/>
                        <Toaster
                            position="top-right"
                            gutter={8}
                            toastOptions={{
                                duration: 4000,
                                style: {
                                    background: 'var(--surface-1)',
                                    color: 'var(--text-primary)',
                                    border: '1px solid var(--border-subtle)',
                                    borderRadius: 'var(--radius-md)',
                                    fontSize: '13px',
                                    boxShadow: 'var(--shadow-md)',
                                },
                                success: { iconTheme: { primary: 'var(--success-strong)', secondary: 'var(--surface-1)' } },
                                error:   { iconTheme: { primary: 'var(--danger-strong)',  secondary: 'var(--surface-1)' } },
                            }}
                        />
                    </AuthProvider>
                </Router>
            </DensityProvider>
        </ThemeProvider>
    );
};

export default App;
